import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from "components/ui/select";
import { Switch } from "components/ui/switch";
import { Textarea } from "components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from "zod";
import { toast } from "sonner";
import { cn } from "lib/utils";
import { productCreateAction } from "action/product-action";
import { categories } from "helpers/constants";
import React, { useState } from "react";
import SiteBreadcrumb from "components/site-breadcrumb";
import { Icon } from "components/ui/icon";
import { useSessionControl } from "hooks/use-session-control";

const schema = z.object({
    name: z.string().min(1, {message: "Your product name is required."}),
    category: z.string().min(1, {message: "Please select a category"}),
    price: z.number().min(1, {message: "Please enter a price"}),
    description: z.string().min(7, {message: "Please enter a description"}),
    featured: z.boolean(),
    todayDeal: z.boolean(),
    thumbnailImage: z
        .instanceof(File, {message: "Please add a Product Image"})
        .refine(file => file instanceof File, {message: "Please add a Product Image"}),
})

const AddProductComponent = () => {
    const [searchCategoryTerm, setSearchCategoryTerm] = useState("");
    const {sessionControl} = useSessionControl();

    // Filter categories based on the search term
    const filteredCategories = categories.filter(category =>
        category.toLowerCase().includes(searchCategoryTerm.toLowerCase())
    );

    const {
        register,
        setValue,
        getValues,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm({
        resolver: zodResolver(schema),
        mode: "all",
        defaultValues: {
            name: "",
            category: "",
            price: 0,
            description: "",
            featured: false,
            todayDeal: false,
            thumbnailImage: null as File
        }
    });

    function onSubmit(data: z.infer<typeof schema>) {
        const toastId = toast.loading("adding product...");

        const formData = new FormData();
        formData.append("name", data.name);
        formData.append("category", data.category);
        formData.append("price", data.price.toString());
        formData.append("description", data.description);
        formData.append("featured", data.featured.toString());
        formData.append("todayDeal", data.todayDeal.toString());
        formData.append("thumbnailImage", data.thumbnailImage);

        // console.log(data);
        productCreateAction(formData).then((response) => {
            if (response.status === 201) {
                toast.dismiss(toastId);
                toast.success("Product created successfully");
                reset();
            } else {

                sessionControl(response.status);

                toast.dismiss(toastId);
                toast.error(response.error);
            }
        }).catch((err: any) => {
            toast.dismiss(toastId);
            toast.error('Failed to create product, An error occurred');
            console.error(err);
        })
    }

    return (
        <div className='w-full'>
            <SiteBreadcrumb/>
            <div className="flex text-xl font-medium text-default-900 whitespace-nowrap mb-3">
                Add Product
            </div>
            <div className=" grid grid-cols-12  gap-4  rounded-lg">
                <div className="col-span-12 md:col-span-7 space-y-4 lg:col-span-7 ">
                    <Card>
                        <CardHeader className="border-b border-solid border-default-200 mb-6">
                            <CardTitle>Product Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">

                            {/*name*/}
                            <div className="flex items-center flex-wrap">
                                <Label className="w-[150px] flex-none" htmlFor="p_Fullname">
                                    Product Name
                                </Label>
                                <Input
                                    id="p_Fullname"
                                    type="text"
                                    placeholder="Name"
                                    {...register("name")}
                                    className={cn("", {
                                        "border-destructive": errors.name
                                    })}
                                />
                            </div>
                            {errors.name && (
                                <p className="text-destructive mt-2 text-sm">{errors.name.message}</p>
                            )}

                            {/*category*/}
                            <div className="flex items-center flex-wrap">
                                <Label className="w-[150px] flex-none">Category</Label>
                                <Select onValueChange={(e) => setValue("category", e)}>
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder="Select Category"/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectLabel>Category</SelectLabel>
                                            {/* Search Input */}
                                            <div className="p-2">
                                                <Input
                                                    type="text"
                                                    placeholder="Search Categories"
                                                    value={searchCategoryTerm}
                                                    onChange={(e) => setSearchCategoryTerm(e.target.value)}
                                                    className="w-full p-1 px-2 border rounded text-sm"
                                                />
                                            </div>
                                            {/* Render filtered categories */}
                                            {filteredCategories.length > 0 ? (
                                                filteredCategories.map((category) => (
                                                    <SelectItem key={category} value={category}
                                                                onSelect={() => setValue("category", category)}>
                                                        {category}
                                                    </SelectItem>
                                                ))
                                            ) : (
                                                <div className="p-2 text-gray-500 text-sm flex justify-center">Can't
                                                    find what you are looking for? Let us know</div>
                                            )}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>

                            {errors.category && (
                                <p className="text-destructive mt-2 text-sm">{errors.category.message}</p>
                            )}

                            {/*price*/}
                            <div className="flex items-center flex-wrap">
                                <Label className="w-[150px] flex-none" htmlFor="price">
                                    Price(₦)
                                </Label>
                                <Input
                                    id="price"
                                    type="number"
                                    placeholder="Price"
                                    className={cn("", {
                                        "border-destructive": errors.price
                                    })}
                                    onChange={
                                        (e) => {
                                            setValue("price", Number(e.target.value));
                                        }
                                    }
                                />
                            </div>
                            {errors.price && (
                                <p className="text-destructive mt-2 text-sm">{errors.price.message}</p>
                            )}

                            {/*minimum purchase qty*/}
                            <div className="flex items-center flex-wrap">
                                <Label className="w-[150px] flex-none" htmlFor="qty">
                                    Minimum Purchase Qty
                                </Label>
                                <Input id="qty" type="text" placeholder="1" disabled={true}/>
                            </div>

                            {/*product tags*/}
                            <div className="flex items-center flex-wrap">
                                <Label className="w-[150px] flex-none" htmlFor="tags">
                                    Tags
                                </Label>
                                <Input
                                    disabled={true}
                                    id="tags"
                                    type="text"
                                    placeholder="Type & hit enter to add a tag"
                                />
                            </div>

                            {/*barcode*/}
                            <div className="flex items-center flex-wrap">
                                <Label className="w-[150px] flex-none" htmlFor="brcode">
                                    Barcode
                                </Label>
                                <Input disabled={true} id="brcode" type="text" placeholder="Code"/>
                            </div>

                            {/*is refundable switch*/}
                            <div className="flex items-center flex-wrap gap-10">
                                <Label className="flex-none w-[132px] ">Refundable</Label>
                                <div className="flex-1">
                                    <Switch disabled={true} color="success"/>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    {/*product description*/}
                    <Card>
                        <CardHeader className="border-b border-solid border-default-200 mb-6">
                            <CardTitle>Product Description</CardTitle>
                        </CardHeader>
                        <CardContent className="flex">
                            <Label className="w-[130px] flex-none">Description</Label>
                            <div className="flex-1">
                                <Textarea
                                    id="pn4"
                                    placeholder="Product description..."
                                    {...register("description")}
                                    className={cn("", {
                                        "border-destructive": errors.description
                                    })}
                                />
                                {errors.description && (
                                    <p className="text-destructive mt-2 text-sm">{errors.description.message}</p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                    {/*product images*/}
                    <Card>
                        <CardHeader className="border-b border-solid border-default-200 mb-6">
                            <CardTitle>Product Images</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/*gallery images*/}
                            {/*<div className="flex items-center space-x-6 rtl:space-x-reverse">*/}
                            {/*    <Label className="inline-flex text-sm font-normal ">*/}
                            {/*        Gallery Images (600 X 600)*/}
                            {/*    </Label>*/}
                            {/*    <div className="flex-1">*/}
                            {/*        <Input*/}
                            {/*            disabled={true}*/}
                            {/*            className="read-only:leading-0 py-1.5 h-full ps-0"*/}
                            {/*            type="file"*/}
                            {/*        />*/}
                            {/*    </div>*/}
                            {/*</div>*/}

                            {/*thumb images*/}
                            <div className="flex items-center space-x-6 rtl:space-x-reverse">
                                <Label className="inline-flex text-sm font-normal ">
                                    Thumb Images (600 X 600)
                                </Label>
                                <div className="flex-1">
                                    <Input
                                        className={cn("read-only:leading-0 py-1.5 h-full ps-0", {
                                            "border-destructive": errors.thumbnailImage
                                        })}
                                        type="file"
                                        onChange={(e) => {
                                            const file = e.target.files[0]; // Get the selected file
                                            if (file) {
                                                // console.log(file); // Do something with the file, like uploading or processing it
                                                setValue("thumbnailImage", file)
                                            }
                                        }}
                                    />
                                    {errors.thumbnailImage && (
                                        <p className="text-destructive mt-2 text-sm">{errors.thumbnailImage.message}</p>
                                    )}

                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/*seo meta tags*/}
                    <Card>
                        <CardHeader className="border-b border-solid border-default-200 mb-6">
                            <CardTitle>SEO Meta Tags</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex">
                                <Label className="w-[130px]">Meta Title</Label>
                                <Input disabled={true} id="meta_title" type="text" placeholder="Title"/>
                            </div>

                            <div className="flex">
                                <Label className="w-[130px]">Description</Label>
                                <Textarea
                                    disabled={true}
                                    id="pn4"
                                    placeholder="Write something for your product seo"
                                />
                            </div>
                            <div className="flex items-center ">
                                <Label className="flex-none w-[130px] ">Meta Image</Label>
                                <Input
                                    disabled={true}
                                    className="read-only:leading-0 py-1.5 h-full ps-0"
                                    type="file"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-span-12 md:col-span-5 space-y-4 lg:col-span-5">

                    {/*low stock warning quantity*/}
                    <Card>
                        <CardHeader className="border-b border-solid border-default-200 mb-6">
                            <CardTitle>Low Stock Quantity Warning</CardTitle>
                        </CardHeader>
                        <CardContent className="flex items-center">
                            <Label className="w-[130px] flex-none">Quantity</Label>
                            <Input disabled={true} id="qty_warning" type="text" placeholder="1"/>
                        </CardContent>
                    </Card>

                    {/*<Card>*/}
                    {/*    <CardHeader className="border-b border-solid border-default-200 mb-6">*/}
                    {/*        <CardTitle>Cash On Delivery</CardTitle>*/}
                    {/*    </CardHeader>*/}
                    {/*    <CardContent className="space-y-4">*/}
                    {/*        <div className="flex justify-between">*/}
                    {/*            <Label className="text-sm font-normal">Status</Label>*/}
                    {/*            <div>*/}
                    {/*                <Switch color="success" />*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*    </CardContent>*/}
                    {/*</Card>*/}
                    <Card>
                        <CardHeader className="border-b border-solid border-default-200 mb-6">
                            <CardTitle>Featured</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between">
                                <Label className="text-sm font-normal">Status</Label>
                                <div>
                                    <Switch onClick={
                                        () => {
                                            setValue("featured", !getValues("featured"));
                                            let x = getValues("featured");
                                        }
                                    } color="success"/>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="border-b border-solid border-default-200 mb-6">
                            <CardTitle>Today's Deal</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between">
                                <Label className="text-sm font-normal">Status</Label>
                                <div>
                                    <Switch onClick={
                                        () => {
                                            setValue("todayDeal", !getValues("todayDeal"));
                                            let x = getValues("todayDeal");
                                        }
                                    } color="success"/>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    {/*<Card>*/}
                    {/*    <CardHeader className="border-b border-solid border-default-200 mb-6">*/}
                    {/*        <CardTitle>Flash Deal</CardTitle>*/}
                    {/*    </CardHeader>*/}
                    {/*    <CardContent className="space-y-4">*/}
                    {/*        <div className="flex items-center">*/}
                    {/*            <Label className="w-[150px] flex-none">Discount</Label>*/}
                    {/*            <Input id="discount" type="text" placeholder="0"/>*/}
                    {/*        </div>*/}

                    {/*        <div className="flex items-center">*/}
                    {/*            <Label className="w-[150px] flex-none">Type</Label>*/}
                    {/*            <Select>*/}
                    {/*                <SelectTrigger className="flex-1">*/}
                    {/*                    <SelectValue placeholder="Select Type"/>*/}
                    {/*                </SelectTrigger>*/}
                    {/*                <SelectContent>*/}
                    {/*                    <SelectGroup>*/}
                    {/*                        <SelectLabel>Type</SelectLabel>*/}
                    {/*                        <SelectItem value="apple">Apple</SelectItem>*/}
                    {/*                        <SelectItem value="banana">Banana</SelectItem>*/}
                    {/*                        <SelectItem value="blueberry">Blueberry</SelectItem>*/}
                    {/*                        <SelectItem value="grapes">Grapes</SelectItem>*/}
                    {/*                        <SelectItem value="pineapple">Pineapple</SelectItem>*/}
                    {/*                    </SelectGroup>*/}
                    {/*                </SelectContent>*/}
                    {/*            </Select>*/}
                    {/*        </div>*/}
                    {/*    </CardContent>*/}
                    {/*</Card>*/}

                </div>
                <div className="col-span-12 flex justify-end">
                    <Button onClick={handleSubmit(onSubmit)}>Save Product</Button>
                </div>
            </div>
        </div>
    );
};

export default AddProductComponent;
